import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import InstagramTutorialOverlay from "./InstagramTutorialOverlay";
import {
  INSTAGRAM_TUTORIAL_REPLAY_KEY,
  MENU_GROUP_DESCRIPTIONS,
  MENU_ITEM_DESCRIPTIONS,
  POPUP_TUTORIALS,
  getPageTutorial,
  getTutorialByCode,
} from "./instagramTutorialDefinitions";
import {
  completeTutorial,
  dismissTutorial,
  dismissTutorialKeepalive,
  loadTutorialBootstrap,
  saveTutorialProgress,
  startTutorial,
} from "./instagramTutorialApi";

export const InstagramTutorialContext = createContext(null);
const INSTAGRAM_HOME_ROUTE = "/shop/instagram/InstagramAutoManageHome";
const isAutoSetupRoute = (pathname) => (
  pathname === "/shop/instagram/InstagramAutoSetupStarter"
  || pathname === "/shop/instagram/InstagramAutoSetupResult"
);
const MENU_TUTORIAL_CODE = "MENU_OVERVIEW";
const MENU_TUTORIAL_VERSION = 4;
const TARGET_WAIT_TIMEOUT = 8000;
const TUTORIAL_INACTIVITY_TIMEOUT = 10 * 60 * 1000;
const TUTORIAL_LOCATION_CHANGE_EVENT = "instagram-tutorial:locationchange";
const POPUP_SCAN_THROTTLE_MS = 100;
const POPUP_TUTORIAL_LIST = Object.keys(POPUP_TUTORIALS).map((code) => POPUP_TUTORIALS[code]);

const isVisible = (element) => {
  if (!element) return false;
  const style = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  return style.display !== "none" && style.visibility !== "hidden" && rect.width > 0 && rect.height > 0;
};

const getVisibleElement = (selector) => Array.from(document.querySelectorAll(selector)).find(isVisible) || null;

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const normalizeMenuTitle = (value) => String(value || "")
  .replace(/\s+/g, "")
  .trim();

const getMenuDescription = (definitions, title) => {
  if (!definitions) return "";
  if (definitions[title]) return definitions[title];

  const normalizedTitle = normalizeMenuTitle(title);
  const matchedKey = Object.keys(definitions).find((key) => normalizeMenuTitle(key) === normalizedTitle);
  return matchedKey ? definitions[matchedKey] : "";
};

const executeTutorialAction = async (action) => {
  if (!action) return;

  if (action.type === "event" && action.name) {
    window.dispatchEvent(new CustomEvent(action.name, { detail: action.detail || {} }));
    // 액션으로 React 화면 상태가 바뀐 뒤 타겟이 렌더링될 시간을 줍니다.
    await wait(120);
  }
};

const prepareMenuForTutorial = async () => {
  if (window.innerWidth <= 800) {
    let mobileMenu = document.querySelector(".mobile_side");
    const startedAt = Date.now();
    while (!mobileMenu && Date.now() - startedAt < TARGET_WAIT_TIMEOUT) {
      await wait(120);
      mobileMenu = document.querySelector(".mobile_side");
    }
    if (mobileMenu && !mobileMenu.classList.contains("active")) {
      const openButton = document.querySelector(".mobile_header .icon_hambuger");
      if (openButton) openButton.click();
      await wait(300);
    }
    return;
  }

  const reducedMenu = document.querySelector(".pc_left_menu_reduction");
  if (reducedMenu && isVisible(reducedMenu)) {
    const expandButton = reducedMenu.querySelector(".menu_reduction_state");
    if (expandButton) expandButton.click();
    await wait(300);
  }
};

const closePreparedMobileMenu = () => {
  if (window.innerWidth > 800) return;
  const mobileMenu = document.querySelector(".mobile_side.active");
  const closeButton = mobileMenu ? mobileMenu.querySelector(".icon_arrow_left") : null;
  if (closeButton) closeButton.click();
};

const waitForTutorialReady = async (timeout = TARGET_WAIT_TIMEOUT) => {
  const startedAt = Date.now();
  let pendingRoot = document.querySelector('[data-instagram-tutorial-ready="false"]');

  while (pendingRoot && Date.now() - startedAt < timeout) {
    await wait(120);
    pendingRoot = document.querySelector('[data-instagram-tutorial-ready="false"]');
  }
};

const waitForResolvedSteps = async (tutorial, timeout = TARGET_WAIT_TIMEOUT) => {
  await waitForTutorialReady(timeout);

  const startedAt = Date.now();
  let resolvedSteps = [];
  let lastSignature = "";
  let stableCount = 0;

  // 팝업은 외곽(헤더/사이드바/푸터)이 먼저 렌더링되고
  // 프롬프트·옵션 영역이 뒤늦게 붙는 경우가 있다.
  // 첫 번째 요소가 보였다고 즉시 확정하지 않고, 보이는 단계 목록이
  // 몇 차례 연속 동일할 때까지 기다린 뒤 튜토리얼 단계를 확정한다.
  while (Date.now() - startedAt < timeout) {
    const currentSteps = tutorial.steps.filter((item) => (
      // 현재 화면에서 이미 보이지 않는 일반 step은 시작 전에 제외합니다.
      // enterAction이 있는 step은 액션 후 나타날 수 있으므로 일단 유지하고,
      // 실제 진입 후에도 타겟이 없으면 런타임에서 해당 step만 제거합니다.
      item.enterAction || getVisibleElement(item.selector)
    ));
    const signature = currentSteps.map((item) => item.code).join("|");

    if (currentSteps.length > 0 && signature === lastSignature) {
      stableCount += 1;
    } else {
      stableCount = 0;
      lastSignature = signature;
      resolvedSteps = currentSteps;
    }

    if (currentSteps.length > 0) {
      resolvedSteps = currentSteps;
      if (stableCount >= 4) break;
    }

    await wait(120);
  }

  return { ...tutorial, steps: resolvedSteps };
};

const normalizeProgress = (progressList) => {
  const result = {};

  (progressList || []).forEach((item) => {
    const code = item.tutorial_code || item.TUTORIAL_CODE;
    if (!code) return;

    const current = result[code];
    if (!current) {
      result[code] = item;
      return;
    }

    const currentStatus = current.progress_status || current.PROGRESS_STATUS || "I";
    const nextStatus = item.progress_status || item.PROGRESS_STATUS || "I";

    // 열람 여부는 tutorial_code 기준입니다.
    // 과거 어떤 버전에서든 완료(C)했다면 이후 버전이 생겨도 완료 상태를 유지합니다.
    if (currentStatus === "C") return;
    if (nextStatus === "C") {
      result[code] = item;
      return;
    }

    // 완료 이력이 없다면 가장 최신 버전의 상태를 대표 상태로 사용합니다.
    const currentVersion = Number(current.tutorial_version || current.TUTORIAL_VERSION || 1);
    const nextVersion = Number(item.tutorial_version || item.TUTORIAL_VERSION || 1);
    if (nextVersion > currentVersion) result[code] = item;
  });

  return result;
};

const getMenuTutorial = () => {
  const menuScope = window.innerWidth <= 800 ? ".mobile_side" : ".pc_left_menu";
  const candidates = Array.from(document.querySelectorAll(
    `${menuScope} [data-instagram-tutorial-menu-home], ${menuScope} [data-instagram-tutorial-menu-group], ${menuScope} [data-instagram-tutorial-menu-item]`
  )).filter(isVisible);

  const seen = new Set();
  const steps = [];

  candidates.forEach((element) => {
    const home = element.getAttribute("data-instagram-tutorial-menu-home");
    const groupCode = element.getAttribute("data-instagram-tutorial-menu-group");
    const itemCode = element.getAttribute("data-instagram-tutorial-menu-item");
    const uniqueCode = home ? "HOME" : groupCode ? `GROUP_${groupCode}` : `ITEM_${itemCode}`;
    if (!uniqueCode || seen.has(uniqueCode)) return;
    seen.add(uniqueCode);

    const title = (element.getAttribute("data-instagram-tutorial-title") || element.textContent || "").trim();
    const description = (element.getAttribute("data-instagram-tutorial-description") || "").trim();
    const attributeSelector = home
      ? '[data-instagram-tutorial-menu-home="true"]'
      : groupCode
        ? `[data-instagram-tutorial-menu-group="${groupCode}"]`
        : `[data-instagram-tutorial-menu-item="${itemCode}"]`;

    steps.push({
      code: uniqueCode,
      selector: attributeSelector,
      title: home ? "인스타그램 홈" : title,
      content: home
        ? "인스타그램 자동관리의 주요 기능과 튜토리얼 다시보기를 확인하는 시작 화면입니다."
        : groupCode
          ? (getMenuDescription(MENU_GROUP_DESCRIPTIONS, title) || description || `${title} 관련 기능을 모아둔 메뉴입니다.`)
          : (getMenuDescription(MENU_ITEM_DESCRIPTIONS, title) || description || `${title} 화면으로 이동합니다.`),
      placement: window.innerWidth <= 800 ? "auto" : "right",
    });
  });

  return {
    code: MENU_TUTORIAL_CODE,
    title: "왼쪽 메뉴 둘러보기",
    group: "메뉴",
    route: window.location.pathname,
    version: MENU_TUTORIAL_VERSION,
    steps,
  };
};

const waitForMenuTutorial = async (timeout = TARGET_WAIT_TIMEOUT) => {
  const startedAt = Date.now();
  let tutorial = getMenuTutorial();
  while (!tutorial.steps.length && Date.now() - startedAt < timeout) {
    await wait(120);
    tutorial = getMenuTutorial();
  }
  return tutorial;
};

const InstagramTutorialProvider = ({ children }) => {
  const [bootstrap, setBootstrap] = useState(null);
  const [progressMap, setProgressMap] = useState({});
  const [activeTutorial, setActiveTutorial] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [entryType, setEntryType] = useState("A");
  const [tutorialSessionId, setTutorialSessionId] = useState("");
  const [currentPathname, setCurrentPathname] = useState(() => window.location.pathname);
  // API 조회는 병렬 가능하지만 홈의 자동 튜토리얼 시작은 Setup 진입 판단 뒤에만 허용합니다.
  const [homeSetupEntryReady, setHomeSetupEntryReady] = useState(
    () => window.location.pathname !== INSTAGRAM_HOME_ROUTE
  );
  const homeSetupEntryReadyRef = useRef(window.location.pathname !== INSTAGRAM_HOME_ROUTE);
  const activeRef = useRef(null);
  const openingRef = useRef(false);
  const popupWasVisibleRef = useRef(false);
  const progressRef = useRef({});
  const autoFlowStartedRef = useRef(false);
  const stepIndexRef = useRef(0);
  const entryTypeRef = useRef("A");
  const tutorialSessionIdRef = useRef("");
  const inactivityTimerRef = useRef(null);
  const lastActivityAtRef = useRef(0);
  const inactiveRef = useRef(false);

  const isInstagramPath = currentPathname.indexOf("/shop/instagram/") === 0;
  const isHomeSetupEntryPending = currentPathname === INSTAGRAM_HOME_ROUTE && !homeSetupEntryReady;
  const isOnAutoSetupRoute = isAutoSetupRoute(currentPathname);

  const resolveHomeSetupEntry = useCallback(() => {
    if (window.location.pathname !== INSTAGRAM_HOME_ROUTE) return;
    homeSetupEntryReadyRef.current = true;
    setHomeSetupEntryReady(true);
  }, []);

  const canOpenTutorialOnCurrentRoute = useCallback((entryType) => {
    const pathname = window.location.pathname;
    // 홈 판단 대기 중에는 수동/자동 모두 실행하지 않습니다.
    if (pathname === INSTAGRAM_HOME_ROUTE && !homeSetupEntryReadyRef.current) return false;
    // Setup으로 이동한 다음에는 자동 튜토리얼이 이어서 실행되지 않게 합니다.
    if (entryType === "A" && isAutoSetupRoute(pathname)) return false;
    return true;
  }, []);

  useEffect(() => {
    activeRef.current = activeTutorial;
  }, [activeTutorial]);

  useEffect(() => {
    progressRef.current = progressMap;
  }, [progressMap]);

  useEffect(() => {
    stepIndexRef.current = stepIndex;
  }, [stepIndex]);

  useEffect(() => {
    entryTypeRef.current = entryType;
  }, [entryType]);

  useEffect(() => {
    tutorialSessionIdRef.current = tutorialSessionId;
  }, [tutorialSessionId]);

  const hasProgress = useCallback((tutorial) => {
    return !!progressRef.current[tutorial.code];
  }, []);

  const updateLocalProgress = useCallback((tutorial, values) => {
    const key = tutorial.code;
    setProgressMap((previous) => ({
      ...previous,
      [key]: { ...(previous[key] || {}), tutorial_code: tutorial.code, tutorial_version: tutorial.version || 1, ...values },
    }));
  }, []);

  const getCommonPayload = useCallback((tutorial, index, type, sessionId) => {
    const currentStep = tutorial.steps[index] || tutorial.steps[0];
    return {
      tutorial_version: tutorial.version || 1,
      tutorial_session_id: sessionId,
      entry_type: type,
      step_no: index + 1,
      step_code: currentStep ? currentStep.code : "",
      total_step_count: tutorial.steps.length,
      page_url: `${window.location.pathname}${window.location.search}`,
      device_type: window.innerWidth <= 800 ? "MOBILE" : "PC",
    };
  }, []);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      window.clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const markActiveTutorialInactive = useCallback(async () => {
    const tutorial = activeRef.current;
    if (!tutorial) return;

    const key = tutorial.code;
    const previousStatus = progressRef.current[key]?.progress_status;
    if (previousStatus === "C" || inactiveRef.current) return;

    inactiveRef.current = true;
    clearInactivityTimer();

    const currentIndex = stepIndexRef.current;
    const payload = getCommonPayload(
      tutorial,
      currentIndex,
      entryTypeRef.current,
      tutorialSessionIdRef.current
    );

    updateLocalProgress(tutorial, {
      progress_status: "D",
      last_step_no: currentIndex + 1,
      max_step_no: Math.max(
        currentIndex + 1,
        Number(progressRef.current[key]?.max_step_no || 0)
      ),
    });

    try {
      await dismissTutorial(tutorial.code, payload);
    } catch (error) {
      console.error("튜토리얼 비활성 이력 저장 실패", error);
    }
  }, [clearInactivityTimer, getCommonPayload, updateLocalProgress]);

  const scheduleInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    if (!activeRef.current) return;

    lastActivityAtRef.current = Date.now();
    inactiveRef.current = false;
    inactivityTimerRef.current = window.setTimeout(() => {
      markActiveTutorialInactive();
    }, TUTORIAL_INACTIVITY_TIMEOUT);
  }, [clearInactivityTimer, markActiveTutorialInactive]);

  const reactivateActiveTutorial = useCallback((tutorial, index) => {
    if (!tutorial) return;

    const key = tutorial.code;
    const previousStatus = progressRef.current[key]?.progress_status;
    if (previousStatus !== "C") {
      updateLocalProgress(tutorial, {
        progress_status: "I",
        last_step_no: index + 1,
        max_step_no: Math.max(
          index + 1,
          Number(progressRef.current[key]?.max_step_no || 0)
        ),
      });
    }

    scheduleInactivityTimer();
  }, [scheduleInactivityTimer, updateLocalProgress]);

  const openTutorial = useCallback(async (tutorial, requestedEntryType = "A") => {
    if (!tutorial || activeRef.current || openingRef.current || !canOpenTutorialOnCurrentRoute(requestedEntryType)) return false;
    openingRef.current = true;

    const resolvedTutorial = await waitForResolvedSteps(tutorial);
    if (!resolvedTutorial.steps.length || activeRef.current || !canOpenTutorialOnCurrentRoute(requestedEntryType)) {
      openingRef.current = false;
      return false;
    }

    await executeTutorialAction(resolvedTutorial.steps[0] && resolvedTutorial.steps[0].enterAction);
    if (!canOpenTutorialOnCurrentRoute(requestedEntryType)) {
      openingRef.current = false;
      return false;
    }

    const firstPayload = getCommonPayload(resolvedTutorial, 0, requestedEntryType, "");
    let sessionId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    try {
      const response = await startTutorial(resolvedTutorial.code, firstPayload);
      sessionId = response.tutorial_session_id || sessionId;
    } catch (error) {
      console.error("튜토리얼 시작 이력 저장 실패", error);
    }

    const previousStatus = progressRef.current[resolvedTutorial.code]?.progress_status;
    updateLocalProgress(resolvedTutorial, {
      progress_status: previousStatus === "C" ? "C" : "I",
      last_entry_type: requestedEntryType,
      last_step_no: 1,
      max_step_no: Math.max(1, Number(progressRef.current[resolvedTutorial.code]?.max_step_no || 0)),
      total_step_count: resolvedTutorial.steps.length,
    });
    entryTypeRef.current = requestedEntryType;
    tutorialSessionIdRef.current = sessionId;
    stepIndexRef.current = 0;
    setEntryType(requestedEntryType);
    setTutorialSessionId(sessionId);
    setStepIndex(0);
    activeRef.current = resolvedTutorial;
    setActiveTutorial(resolvedTutorial);
    openingRef.current = false;
    scheduleInactivityTimer();
    return true;
  }, [canOpenTutorialOnCurrentRoute, getCommonPayload, scheduleInactivityTimer, updateLocalProgress]);

  const tryOpenCurrentPageTutorial = useCallback(async () => {
    const pageTutorial = getPageTutorial();
    if (!pageTutorial || hasProgress(pageTutorial) || activeRef.current) return false;
    return openTutorial(pageTutorial, "A");
  }, [hasProgress, openTutorial]);

  const tryOpenDirectTutorial = useCallback(async () => {
    if (activeRef.current) return false;
    const visiblePopup = Object.keys(POPUP_TUTORIALS)
      .map((code) => POPUP_TUTORIALS[code])
      .find((tutorial) => getVisibleElement(tutorial.popupSelector));

    if (visiblePopup && bootstrap && bootstrap.auto_target_yn === "Y" && !hasProgress(visiblePopup)) {
      return openTutorial(visiblePopup, "A");
    }
    if (visiblePopup) return false;
    return tryOpenCurrentPageTutorial();
  }, [bootstrap, hasProgress, openTutorial, tryOpenCurrentPageTutorial]);

  const closeActiveTutorial = useCallback(async () => {
    if (!activeRef.current) return;
    const tutorial = activeRef.current;
    const currentIndex = stepIndexRef.current;
    const key = tutorial.code;
    const payload = getCommonPayload(
      tutorial,
      currentIndex,
      entryTypeRef.current,
      tutorialSessionIdRef.current
    );
    const previousStatus = progressRef.current[key]?.progress_status;

    clearInactivityTimer();
    inactiveRef.current = false;

    try {
      await dismissTutorial(tutorial.code, payload);
    } catch (error) {
      console.error("튜토리얼 그만보기 이력 저장 실패", error);
    }

    await executeTutorialAction(tutorial.exitAction);

    updateLocalProgress(tutorial, {
      progress_status: previousStatus === "C" ? "C" : "D",
      last_step_no: currentIndex + 1,
      max_step_no: Math.max(
        currentIndex + 1,
        Number(progressRef.current[key]?.max_step_no || 0)
      ),
    });
    setActiveTutorial(null);
    activeRef.current = null;

    if (tutorial.code === MENU_TUTORIAL_CODE) {
      closePreparedMobileMenu();
    }
  }, [clearInactivityTimer, getCommonPayload, updateLocalProgress]);

  const finishActiveTutorial = useCallback(async () => {
    if (!activeRef.current) return;
    const tutorial = activeRef.current;
    const currentIndex = stepIndexRef.current;
    const currentEntryType = entryTypeRef.current;
    const key = tutorial.code;
    const payload = getCommonPayload(
      tutorial,
      currentIndex,
      currentEntryType,
      tutorialSessionIdRef.current
    );

    clearInactivityTimer();
    inactiveRef.current = false;

    try {
      await completeTutorial(tutorial.code, payload);
    } catch (error) {
      console.error("튜토리얼 완료 이력 저장 실패", error);
    }

    await executeTutorialAction(tutorial.exitAction);

    updateLocalProgress(tutorial, {
      progress_status: "C",
      last_step_no: currentIndex + 1,
      max_step_no: Math.max(
        currentIndex + 1,
        Number(progressRef.current[key]?.max_step_no || 0)
      ),
    });
    setActiveTutorial(null);
    activeRef.current = null;

    if (tutorial.code === MENU_TUTORIAL_CODE) {
      closePreparedMobileMenu();
      if (currentEntryType === "A") {
        window.setTimeout(() => tryOpenDirectTutorial(), 350);
      }
    }
  }, [clearInactivityTimer, getCommonPayload, tryOpenDirectTutorial, updateLocalProgress]);

  /**
   * 현재 step의 타겟이 실제 화면에 끝까지 나타나지 않았을 때 호출됩니다.
   *
   * 특정 솔루션이나 selector를 별도로 나열하지 않고,
   * 확인된 현재 step만 activeTutorial.steps에서 제거합니다.
   * 그 결과 step 번호와 total_step_count가 즉시 다시 계산됩니다.
   *
   * NEXT 방향에서 제거되면 배열이 당겨진 같은 index를 계속 확인하고,
   * PREV 방향에서 제거되면 한 칸 앞 index로 이동합니다.
   */
  const removeUnavailableStep = useCallback(async (navigationDirection) => {
    if (!activeRef.current) return;

    const tutorial = activeRef.current;
    const currentIndex = stepIndexRef.current;
    if (!tutorial.steps[currentIndex]) return;

    const remainingSteps = tutorial.steps.filter((_, index) => index !== currentIndex);

    // 실제로 보여줄 step이 하나도 남지 않았다면 튜토리얼을 정상 완료합니다.
    if (!remainingSteps.length) {
      await finishActiveTutorial();
      return;
    }

    const nextTutorial = {
      ...tutorial,
      steps: remainingSteps,
    };

    const nextIndex = navigationDirection === "previous"
      ? Math.max(0, currentIndex - 1)
      : Math.min(currentIndex, remainingSteps.length - 1);

    // 연속 미노출 step도 항상 최신 steps 배열을 기준으로 처리하도록 ref를 먼저 갱신합니다.
    activeRef.current = nextTutorial;
    stepIndexRef.current = nextIndex;
    setActiveTutorial(nextTutorial);
    setStepIndex(nextIndex);

    reactivateActiveTutorial(nextTutorial, nextIndex);

    const key = nextTutorial.code;
    const previousMaxStep = Number(progressRef.current[key]?.max_step_no || 0);
    updateLocalProgress(nextTutorial, {
      last_step_no: nextIndex + 1,
      max_step_no: Math.min(
        remainingSteps.length,
        Math.max(nextIndex + 1, previousMaxStep)
      ),
      total_step_count: remainingSteps.length,
    });

    const nextStepDefinition = remainingSteps[nextIndex];
    await executeTutorialAction(nextStepDefinition && nextStepDefinition.enterAction);

    const payload = {
      ...getCommonPayload(
        nextTutorial,
        nextIndex,
        entryTypeRef.current,
        tutorialSessionIdRef.current
      ),
      event_type: navigationDirection === "previous" ? "PREV" : "NEXT",
    };

    try {
      await saveTutorialProgress(nextTutorial.code, payload);
    } catch (error) {
      console.error("튜토리얼 미노출 단계 건너뛰기 이력 저장 실패", error);
    }
  }, [
    finishActiveTutorial,
    getCommonPayload,
    reactivateActiveTutorial,
    updateLocalProgress,
  ]);

  const moveStep = useCallback(async (direction) => {
    if (!activeRef.current) return;
    const tutorial = activeRef.current;
    const currentIndex = stepIndexRef.current;
    const nextIndex = direction === "PREV"
      ? Math.max(0, currentIndex - 1)
      : Math.min(tutorial.steps.length - 1, currentIndex + 1);
    if (nextIndex === currentIndex) return;

    const nextStepDefinition = tutorial.steps[nextIndex];
    await executeTutorialAction(nextStepDefinition && nextStepDefinition.enterAction);
    stepIndexRef.current = nextIndex;
    setStepIndex(nextIndex);

    reactivateActiveTutorial(tutorial, nextIndex);

    const payload = {
      ...getCommonPayload(
        tutorial,
        nextIndex,
        entryTypeRef.current,
        tutorialSessionIdRef.current
      ),
      event_type: direction,
    };

    try {
      await saveTutorialProgress(tutorial.code, payload);
    } catch (error) {
      console.error("튜토리얼 단계 이력 저장 실패", error);
    }
  }, [getCommonPayload, reactivateActiveTutorial]);

  const startReplay = useCallback(async (tutorialCode) => {
    if (tutorialCode === MENU_TUTORIAL_CODE) {
      await prepareMenuForTutorial();
    }
    const tutorial = tutorialCode === MENU_TUTORIAL_CODE ? await waitForMenuTutorial() : getTutorialByCode(tutorialCode);
    if (!tutorial) return;

    if (tutorialCode === MENU_TUTORIAL_CODE) {
      await openTutorial(tutorial, "M");
      return;
    }

    const isPopupTutorial = !!POPUP_TUTORIALS[tutorialCode];
    const targetRoute = isPopupTutorial ? tutorial.replayRoute : tutorial.route;
    const currentRouteMatches = window.location.pathname === tutorial.route;
    const popupAlreadyOpen = isPopupTutorial && getVisibleElement(tutorial.popupSelector);

    if (popupAlreadyOpen || (!isPopupTutorial && currentRouteMatches)) {
      await openTutorial(tutorial, "M");
      return;
    }

    sessionStorage.setItem(INSTAGRAM_TUTORIAL_REPLAY_KEY, JSON.stringify({
      tutorial_code: tutorialCode,
      requested_at: Date.now(),
    }));
    window.location.href = targetRoute;
  }, [openTutorial]);

  useEffect(() => {
    const markInactiveIfTimedOut = () => {
      if (!activeRef.current || inactiveRef.current || !lastActivityAtRef.current) return;
      if (Date.now() - lastActivityAtRef.current >= TUTORIAL_INACTIVITY_TIMEOUT) {
        markActiveTutorialInactive();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") markInactiveIfTimedOut();
    };

    window.addEventListener("focus", markInactiveIfTimedOut);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", markInactiveIfTimedOut);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [markActiveTutorialInactive]);

  useEffect(() => {
    const handlePageHide = () => {
      const tutorial = activeRef.current;
      if (!tutorial) return;

      const key = tutorial.code;
      if (progressRef.current[key]?.progress_status === "C") return;

      const currentIndex = stepIndexRef.current;
      inactiveRef.current = true;
      clearInactivityTimer();
      updateLocalProgress(tutorial, {
        progress_status: "D",
        last_step_no: currentIndex + 1,
        max_step_no: Math.max(
          currentIndex + 1,
          Number(progressRef.current[key]?.max_step_no || 0)
        ),
      });

      const payload = getCommonPayload(
        tutorial,
        currentIndex,
        entryTypeRef.current,
        tutorialSessionIdRef.current
      );
      dismissTutorialKeepalive(tutorial.code, payload);
    };

    window.addEventListener("pagehide", handlePageHide);
    return () => window.removeEventListener("pagehide", handlePageHide);
  }, [clearInactivityTimer, getCommonPayload, updateLocalProgress]);

  useEffect(() => {
    let lastPathname = window.location.pathname;
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const notifyLocationChange = () => {
      window.dispatchEvent(new Event(TUTORIAL_LOCATION_CHANGE_EVENT));
    };

    const wrappedPushState = function (...args) {
      const result = originalPushState.apply(this, args);
      notifyLocationChange();
      return result;
    };

    const wrappedReplaceState = function (...args) {
      const result = originalReplaceState.apply(this, args);
      notifyLocationChange();
      return result;
    };

    const handleLocationChange = () => {
      const nextPathname = window.location.pathname;
      if (nextPathname === lastPathname) return;
      lastPathname = nextPathname;
      // SPA 내 홈 진입 시에도 서버 확인을 다시 마치기 전에는 튜토리얼을 열지 않습니다.
      if (nextPathname === INSTAGRAM_HOME_ROUTE) {
        homeSetupEntryReadyRef.current = false;
        setHomeSetupEntryReady(false);
        autoFlowStartedRef.current = false;
      }
      setCurrentPathname(nextPathname);
      if (activeRef.current) closeActiveTutorial();
    };

    window.history.pushState = wrappedPushState;
    window.history.replaceState = wrappedReplaceState;
    window.addEventListener("popstate", notifyLocationChange);
    window.addEventListener(TUTORIAL_LOCATION_CHANGE_EVENT, handleLocationChange);

    return () => {
      if (window.history.pushState === wrappedPushState) window.history.pushState = originalPushState;
      if (window.history.replaceState === wrappedReplaceState) window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", notifyLocationChange);
      window.removeEventListener(TUTORIAL_LOCATION_CHANGE_EVENT, handleLocationChange);
    };
  }, [closeActiveTutorial]);

  useEffect(() => () => {
    clearInactivityTimer();
  }, [clearInactivityTimer]);

  useEffect(() => {
    if (!isInstagramPath) return undefined;
    let mounted = true;

    loadTutorialBootstrap()
      .then((data) => {
        if (!mounted) return;
        const normalizedProgress = normalizeProgress(data.progress_list || []);
        progressRef.current = normalizedProgress;
        setBootstrap(data);
        setProgressMap(normalizedProgress);
      })
      .catch((error) => {
        console.error("튜토리얼 상태 조회 실패", error);
        if (mounted) setBootstrap({ status: "error", auto_target_yn: "N", progress_list: [] });
      });

    return () => { mounted = false; };
  }, [isInstagramPath]);

  useEffect(() => {
    if (!bootstrap || autoFlowStartedRef.current || !isInstagramPath || isHomeSetupEntryPending || isOnAutoSetupRoute) return;
    autoFlowStartedRef.current = true;

    const runInitialFlow = async () => {
      await wait(350);

      let replayRequest = null;
      try {
        replayRequest = JSON.parse(sessionStorage.getItem(INSTAGRAM_TUTORIAL_REPLAY_KEY) || "null");
      } catch (error) {
        replayRequest = null;
      }

      if (replayRequest && Date.now() - Number(replayRequest.requested_at || 0) < 300000) {
        const requestedTutorial = getTutorialByCode(replayRequest.tutorial_code);
        if (requestedTutorial) {
          const started = await openTutorial(requestedTutorial, "M");
          if (started) sessionStorage.removeItem(INSTAGRAM_TUTORIAL_REPLAY_KEY);
          return;
        }
      } else if (replayRequest) {
        sessionStorage.removeItem(INSTAGRAM_TUTORIAL_REPLAY_KEY);
      }

      if (bootstrap.auto_target_yn !== "Y") return;

      const menuProgressKey = { code: MENU_TUTORIAL_CODE };
      if (!hasProgress(menuProgressKey)) {
        await prepareMenuForTutorial();
        const menuTutorial = await waitForMenuTutorial();
        if (menuTutorial.steps.length > 0) {
          await openTutorial(menuTutorial, "A");
          return;
        }
      }

      await tryOpenDirectTutorial();
    };

    runInitialFlow();
  }, [bootstrap, hasProgress, isInstagramPath, isHomeSetupEntryPending, isOnAutoSetupRoute, openTutorial, tryOpenDirectTutorial]);

  // 수동 다시보기 이벤트는 팝업 감시 여부와 관계없이 항상 가볍게 유지합니다.
  useEffect(() => {
    if (!isInstagramPath) return undefined;

    const handleReplayEvent = (event) => {
      if (event.detail && event.detail.tutorialCode) startReplay(event.detail.tutorialCode);
    };
    window.addEventListener("instagram-tutorial:replay", handleReplayEvent);

    return () => {
      window.removeEventListener("instagram-tutorial:replay", handleReplayEvent);
    };
  }, [isInstagramPath, startReplay]);

  // 팝업 자동 튜토리얼 감시는 필요한 동안에만 켭니다.
  // 실제 튜토리얼이 떠 있는 동안에는 Provider의 전역 DOM 감시를 완전히 중지해
  // Overlay의 타겟 추적과 경쟁하지 않도록 합니다.
  useEffect(() => {
    if (!bootstrap || !isInstagramPath || activeTutorial || isHomeSetupEntryPending || isOnAutoSetupRoute) return undefined;

    let replayRequest = null;
    try {
      replayRequest = JSON.parse(sessionStorage.getItem(INSTAGRAM_TUTORIAL_REPLAY_KEY) || "null");
    } catch (error) {
      replayRequest = null;
    }

    const replayIsFresh = replayRequest
      && Date.now() - Number(replayRequest.requested_at || 0) < 300000;
    const hasManualPopupReplay = !!(
      replayIsFresh
      && replayRequest.tutorial_code
      && POPUP_TUTORIALS[replayRequest.tutorial_code]
    );
    const hasPendingAutoPopup = bootstrap.auto_target_yn === "Y"
      && POPUP_TUTORIAL_LIST.some((tutorial) => !hasProgress(tutorial));
    const hasVisiblePopup = bootstrap.auto_target_yn === "Y"
      && POPUP_TUTORIAL_LIST.some((tutorial) => getVisibleElement(tutorial.popupSelector));

    // 이미 본 팝업뿐이고 현재 열려 있는 팝업도 없다면 body 감시 자체를 만들지 않습니다.
    if (!hasManualPopupReplay && !hasPendingAutoPopup && !hasVisiblePopup) {
      popupWasVisibleRef.current = false;
      return undefined;
    }

    let observer = null;
    let initialTimer = null;
    let scanTimer = null;
    let scanRunning = false;
    let scanPending = false;
    let disposed = false;

    const tryOpenPopupTutorial = async () => {
      if (disposed || activeRef.current) return;

      let currentReplayRequest = null;
      try {
        currentReplayRequest = JSON.parse(sessionStorage.getItem(INSTAGRAM_TUTORIAL_REPLAY_KEY) || "null");
      } catch (error) {
        currentReplayRequest = null;
      }

      const visiblePopup = POPUP_TUTORIAL_LIST
        .find((tutorial) => getVisibleElement(tutorial.popupSelector));

      if (!visiblePopup) {
        const popupWasVisible = popupWasVisibleRef.current;
        popupWasVisibleRef.current = false;
        if (popupWasVisible && bootstrap.auto_target_yn === "Y") {
          await tryOpenCurrentPageTutorial();
        }
        return;
      }
      popupWasVisibleRef.current = true;

      const isManualReplay = currentReplayRequest
        && Date.now() - Number(currentReplayRequest.requested_at || 0) < 300000
        && currentReplayRequest.tutorial_code === visiblePopup.code;
      if (isManualReplay) {
        const started = await openTutorial(visiblePopup, "M");
        if (started) sessionStorage.removeItem(INSTAGRAM_TUTORIAL_REPLAY_KEY);
        return;
      }

      if (bootstrap.auto_target_yn === "Y" && !hasProgress(visiblePopup)) {
        await openTutorial(visiblePopup, "A");
      }
    };

    const runPopupScan = async () => {
      if (disposed || scanRunning) {
        scanPending = true;
        return;
      }

      scanRunning = true;
      try {
        await tryOpenPopupTutorial();
      } finally {
        scanRunning = false;
        if (!disposed && scanPending && !activeRef.current) {
          scanPending = false;
          schedulePopupScan();
        }
      }
    };

    const schedulePopupScan = () => {
      if (disposed || activeRef.current) return;
      if (scanRunning) {
        scanPending = true;
        return;
      }
      if (scanTimer) return;

      scanTimer = window.setTimeout(() => {
        scanTimer = null;
        runPopupScan();
      }, POPUP_SCAN_THROTTLE_MS);
    };

    observer = new MutationObserver(() => {
      // React가 한 번의 화면 갱신에서 많은 DOM mutation을 만들더라도
      // 팝업 탐색은 최대 100ms에 한 번만 수행합니다.
      schedulePopupScan();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // 첫 화면에 이미 팝업이 렌더링되어 있는 경우를 놓치지 않습니다.
    initialTimer = window.setTimeout(runPopupScan, 150);

    return () => {
      disposed = true;
      if (observer) observer.disconnect();
      if (initialTimer) window.clearTimeout(initialTimer);
      if (scanTimer) window.clearTimeout(scanTimer);
    };
  }, [activeTutorial, bootstrap, hasProgress, isInstagramPath, isHomeSetupEntryPending, isOnAutoSetupRoute, openTutorial, tryOpenCurrentPageTutorial]);

  const contextValue = useMemo(() => ({
    bootstrap,
    progressMap,
    startReplay,
    menuTutorialCode: MENU_TUTORIAL_CODE,
    resolveHomeSetupEntry,
  }), [bootstrap, progressMap, startReplay, resolveHomeSetupEntry]);

  return (
    <InstagramTutorialContext.Provider value={contextValue}>
      {children}
      {activeTutorial ? (
        <InstagramTutorialOverlay
          tutorial={activeTutorial}
          stepIndex={stepIndex}
          onPrevious={() => moveStep("PREV")}
          onNext={() => moveStep("NEXT")}
          onTargetMissing={removeUnavailableStep}
          onDismiss={closeActiveTutorial}
          onComplete={finishActiveTutorial}
        />
      ) : null}
    </InstagramTutorialContext.Provider>
  );
};

export const useInstagramTutorial = () => useContext(InstagramTutorialContext);

export default InstagramTutorialProvider;
